/** @format */

import React, { useCallback, useEffect, useState } from 'react';
import GridContainer from '../../../components/Grid/GridContainer';
import GridItem from '../../../components/Grid/GridItem';
import CountryGroupSelect from './CountryGroupSelect';
import WelcomeSmsContent from './WelcomeSmsContent';
import PlatformResellerSelect from './PlatformResellerSelect';
import Card from '../../../components/Card/Card';
import CardBody from '../../../components/Card/CardBody';
import CustomTextButtom from '../../../components/Buttons/CustomTextButton';
import { ItemLoaders } from '../../../core/loaders/ItemLoaders';
import { FormGeneralHelper } from '../../../helpers/FormGeneral';
import { configDefault } from '../../../core/config';
import { platformGroupsConstants } from '../../../modules/platformGroups/reducers';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Checkbox, makeStyles, Table, TableBody, TableCell, TableHead, TableRow } from '@material-ui/core';
import I18n from '../../../components/Translate/I18n';
import { DISCOUNT_CONSTANTS } from '../../../modules/discounts/reducers';
import Filter from '../../../interfaces/forms/Filter';
import Order from '../../../interfaces/forms/Order';
import adescomStyle from '../../../assets/jss/adescom/adescomStyle';
import EditButton from '../../../components/Buttons/EditButton';
import TextAreaFormRest from '../../../components/Form/TextAreaFormRest';
import WelcomeSmsLegend from './WelcomeSmsLegend';
import CardHeader from '../../../components/Heading/CardHeaderExt';
import AccountTreeIcon from '@material-ui/icons/AccountTree';
import WelcomeSmsZoneForm from './WelcomeSmsZoneForm';
import withStyles from '@material-ui/core/styles/withStyles';
import oplAppStyle from '../../../assets/themes/OPL/jss/App/OplAppStyle';
import DeleteButton from '../../../components/Buttons/DeleteButton';
import { AlertDialog } from '../../lib/dialog/AlertDialog';
import { tariffConstants } from '../../../modules/tariffs/actions';
import { NotificationHelper } from '../../../helpers/NotificationHelper';
import TableFooter from '@material-ui/core/TableFooter';
import Pager from '../../../components/Adescom/PagerRange';
import PlatformResellerInterface from '../../../interfaces/platform/PlatformResellerInterface';
import { PlatformResellerCollectionInterface } from '../../../interfaces/collections/PlatformResellerCollectionInterface';
import ApiCollectionResponseInterface from '../../../interfaces/api/ApiCollectionResponseInterface';

interface WelcomeSmsConfigurationInterface {
    classes: any;
}

export interface LanguageToSmsContentList {
    langId: number;
    smsContent: string;
}

export interface ZoneToSmsTemplate {
    id: number;
    zoneId: number;
    smsContent: string;
    name: string;
    platformResellerIds: string;
    systemLanguageId: number;
}

export interface ZoneToSmsTemplateCollection extends ApiCollectionResponseInterface {
    'hydra:member': Array<ZoneToSmsTemplate>;
}

const WelcomeSmsConfiguration = (props: WelcomeSmsConfigurationInterface): React.ReactElement => {
    const [addMode, setAddMode] = useState<boolean>(false);
    const [zoneAddMode, setZoneAddMode] = useState<boolean>(false);
    const [editId, setEditId] = useState<number | null>(null);
    const [smsContent, setSmsContent] = useState<Array<LanguageToSmsContentList>>([]);
    const [selectedZone, setSelectedZone] = useState<string | null>(null);
    const [selectedPr, setSelectedPr] = useState<Array<number>>([]);
    const [resellers, setResellers] = useState<any>(null);
    const [templates, setTemplates] = useState<ZoneToSmsTemplateCollection | null>(null);
    const [zones, setZones] = useState<any>(null);
    const [editedZone, setEditedZone] = useState<any>(null);
    const [filters, setFilters] = useState<Array<Filter>>([{ name: 'customZoneType', value: 1 }]);
    const [orders, setOrders] = useState<Array<Order>>([]);
    const [smsContentName, setSmsContentName] = useState<string | null>(null);
    const [selectedIdToDelete, setSelectedIdToDelete] = useState<number | null>(null);
    const [deleteMessage, setDeleteMessage] = useState<string | null>(null);
    const [mainReseller, setMainReseller] = useState<PlatformResellerInterface | null>(null);

    const URI = '/zone_to_sms_templates';
    const ZONE_URI = '/zones';
    const PR_URL = '/platform_resellers';
    const { classes } = props;

    const dispatch = useDispatch();

    const tableStickyHeader = { root: classes.tableStickyHeader };

    const saveSmsTemplate = (): void => {
        if (!editId) {
            const outputObject = {
                mainname: smsContentName,
                smsContent: JSON.stringify(smsContent),
                zoneId: selectedZone,
                platformResellerIds: JSON.stringify(selectedPr),
            };
            const itemLoaders = new ItemLoaders(dispatch);
            itemLoaders.postWithCallback(
                FormGeneralHelper.getByUri(FormGeneralHelper.getPath(URI, configDefault.PLATFORM_API_PREFIX)),
                platformGroupsConstants.ADD,
                outputObject,
                (data: any): void => {
                    setAddMode(false);
                    setZoneAddMode(false);
                    loadTemplates();
                },
                {
                    propagateDataToCallback: true,
                    dispatchAfterCallback: false,
                    dispatchErrorAfterCallback: true,
                }
            );
        } else {
            const outputObject = {
                mainname: smsContentName,
                smsContent: JSON.stringify(smsContent),
                zoneId: selectedZone,
                platformResellerIds: JSON.stringify(selectedPr),
            };
            const itemLoaders = new ItemLoaders(dispatch);
            itemLoaders.putWithCallback(
                FormGeneralHelper.getByUri(FormGeneralHelper.getPath(URI + '/' + editId, configDefault.PLATFORM_API_PREFIX)),
                platformGroupsConstants.ADD,
                outputObject,
                (data: any): void => {
                    setAddMode(false);
                    setZoneAddMode(false);
                    loadTemplates();
                },
                {
                    propagateDataToCallback: true,
                    dispatchAfterCallback: false,
                    dispatchErrorAfterCallback: true,
                }
            );
        }
    };

    const firstSerach = useCallback(() => {
        const itemLoader = new ItemLoaders(dispatch);
        itemLoader.getListWithCallback(
            FormGeneralHelper.getByUri(FormGeneralHelper.getPath(PR_URL)),
            DISCOUNT_CONSTANTS.LIST,
            filters,
            orders,
            (data: PlatformResellerCollectionInterface) => {
                if (data && data['hydra:member']) {
                    data['hydra:member'].map((el) => {
                        if (!el.parentResellerId) {
                            setMainReseller(el);
                        }
                    });
                }
                setResellers(data);
            },
            { propagateDataToCallback: true, dispatchAfterCallback: false }
        );
    }, [dispatch, filters, orders]);

    const loadZones = useCallback(
        (uri?: string | null) => {
            if (!uri) {
                uri = FormGeneralHelper.getPath(ZONE_URI);
            } else {
                // Quick fix, We need to put "?" before "&" because we do not use filters here, so uri does not have it. When we dont have "?" in uri system does not recognise path
                uri = uri.replace(/&/g, '?&');
            }
            const itemLoader = new ItemLoaders(dispatch);
            itemLoader.getListWithCallback(
                FormGeneralHelper.getByUri(uri),
                DISCOUNT_CONSTANTS.LIST,
                filters,
                orders,
                (data: any) => {
                    setZones(data);
                },
                { propagateDataToCallback: true, dispatchAfterCallback: false }
            );
        },
        [dispatch, filters, orders]
    );

    const loadTemplates = useCallback(
        (uri?: string | null) => {
            if (!uri) {
                uri = FormGeneralHelper.getPath(URI);
            } else {
                // Quick fix, We need to put "?" before "&" because we do not use filters here, so uri does not have it. When we dont have "?" in uri system does not recognise path
                uri = uri.replace(/&/g, '?&');
            }
            const itemLoader = new ItemLoaders(dispatch);
            itemLoader.getListWithCallback(
                FormGeneralHelper.getByUri(uri),
                DISCOUNT_CONSTANTS.LIST,
                filters,
                orders,
                (data: any) => {
                    setTemplates(data);
                },
                { propagateDataToCallback: true, dispatchAfterCallback: false }
            );
        },
        [dispatch, filters, orders]
    );

    const updateCallback = (
        event: any,
        stateName: string,
        typeField: string,
        typeCallback: string,
        required: boolean,
        vMax: number,
        vMin?: number
    ): void => {
        setSmsContentName(event.target.value);
    };

    const addNew = (): void => {
        setAddMode(true);
    };
    const addNewZone = (): void => {
        setZoneAddMode(true);
    };

    const makeEdit = (params: any): void => {
        setAddMode(true);
        setEditId(params.item.id);
        setSelectedPr(JSON.parse(params.item.platformResellerIds));
        setSelectedZone(params.item.zoneId);
        setSmsContentName(params.item.mainname);
        setSmsContent(JSON.parse(params.item.smsContent));
    };

    const makeZoneEdit = (params: any): void => {
        setZoneAddMode(true);
        setEditedZone(params.item);
    };

    const makeDelete = (params: { id: number }): void => {
        setSelectedIdToDelete(params.id);
        setDeleteMessage('notifications:h.welcome_sms_delete_message');
    };
    const makeZoneDelete = (params: { id: number }): void => {
        setSelectedIdToDelete(params.id);
        setDeleteMessage('notifications:h.zone_delete_message');
    };

    const confirmDelete = (): void => {
        if (deleteMessage === 'notifications:h.zone_delete_message') {
            const itemLoaders = new ItemLoaders(dispatch);
            itemLoaders.deleteCall(
                FormGeneralHelper.getByUri(FormGeneralHelper.getPath(`${ZONE_URI}/${selectedIdToDelete}`, configDefault.PLATFORM_API_PREFIX)),
                tariffConstants.DELETE,
                (data: any) => {
                    setSelectedIdToDelete(null);
                    setDeleteMessage(null);
                    if (!data) {
                        NotificationHelper.notifyError('notifications:notify.zone_delete_error', dispatch);
                    } else {
                        NotificationHelper.notifySuccess('notifications:notify.zone_delete_success', dispatch);
                        loadZones();
                        loadTemplates();
                    }
                },
                { dispatchAfterCallback: false }
            );
            return;
        }

        if (deleteMessage === 'notifications:h.welcome_sms_delete_message') {
            const itemLoaders = new ItemLoaders(dispatch);
            itemLoaders.deleteCall(
                FormGeneralHelper.getByUri(FormGeneralHelper.getPath(`${URI}/${selectedIdToDelete}`, configDefault.PLATFORM_API_PREFIX)),
                tariffConstants.DELETE,
                (data: any) => {
                    setSelectedIdToDelete(null);
                    setDeleteMessage(null);
                    if (!data) {
                        NotificationHelper.notifyError('notifications:notify.welcome_sms_delete_error', dispatch);
                    } else {
                        NotificationHelper.notifySuccess('notifications:notify.welcome_sms_delete_success', dispatch);
                        loadTemplates();
                    }
                },
                { dispatchAfterCallback: false }
            );

            return;
        }
    };

    useEffect(() => {
        firstSerach();
        loadTemplates();
        loadZones();
    }, [firstSerach, loadTemplates, loadZones]);

    if (addMode) {
        return (
            <GridContainer spacing={2}>
                <GridItem xs={4}>
                    <Card>
                        <CardBody>
                            <PlatformResellerSelect classes={props.classes} setPr={setSelectedPr} resellers={resellers} selected={selectedPr} />
                        </CardBody>
                    </Card>
                </GridItem>
                <GridItem xs={8}>
                    <GridContainer spacing={1}>
                        <GridItem xs={12}>
                            <Card>
                                <CardHeader icon={<AccountTreeIcon />} trans="notifications:welcome_sms.legend" />
                                <CardBody>
                                    <WelcomeSmsLegend />
                                </CardBody>
                            </Card>
                        </GridItem>
                        <GridItem xs={12}>
                            <Card>
                                <CardHeader icon={<AccountTreeIcon />} trans="notifications:welcome_sms.details" />
                                <CardBody>
                                    <TextAreaFormRest
                                        stateName={'schemaName'}
                                        callback={updateCallback}
                                        stateObjectState={null}
                                        typeField={'text'}
                                        displayFieldName={'notifications:welcome_sms.schema_name'}
                                        required={false}
                                        value={smsContentName}
                                        vMin={4}
                                        vMax={64}
                                        typeCallback={'number'}
                                    />
                                    <CountryGroupSelect classes={props.classes} setZone={setSelectedZone} selected={selectedZone} />
                                    {mainReseller && templates && (
                                        <WelcomeSmsContent
                                            setSmsContent={setSmsContent}
                                            smsContent={smsContent}
                                            mainReseller={mainReseller}
                                            templates={templates}
                                            editId={editId}
                                        />
                                    )}
                                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                        <CustomTextButtom
                                            size={'lg'}
                                            color={'primary'}
                                            callback={saveSmsTemplate}
                                            callbackParam={{}}
                                            label={'general:common.save'}
                                        />
                                    </div>
                                </CardBody>
                            </Card>
                        </GridItem>
                    </GridContainer>
                </GridItem>
            </GridContainer>
        );
    } else if (zoneAddMode) {
        return <WelcomeSmsZoneForm classes={classes} editZone={editedZone} setZoneAddMode={setZoneAddMode} loadZones={loadZones} />;
    } else {
        return (
            <GridContainer spacing={2}>
                <GridItem xs={12}>
                    <CustomTextButtom
                        size={'lg'}
                        color={'primary'}
                        callback={addNew}
                        callbackParam={{}}
                        label={'notifications:welcome_sms.add_new'}
                    />
                    <CustomTextButtom
                        size={'lg'}
                        color={'primary'}
                        callback={addNewZone}
                        callbackParam={{}}
                        label={'notifications:welcome_sms.add_new_zone'}
                    />
                </GridItem>
                <GridItem xs={6}>
                    <Card>
                        <CardHeader icon={<AccountTreeIcon />} trans="notifications:welcome_sms.schemas" />
                        <CardBody>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell classes={tableStickyHeader} padding="none" align="center" style={{ width: 30 }}>
                                            <I18n trans="notifications:welcome_sms.actions" />
                                        </TableCell>
                                        <TableCell classes={tableStickyHeader} padding="none" align="center">
                                            <I18n trans="notifications:welcome_sms.schema_name" />
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {templates &&
                                        templates['hydra:member'].map((el: any) => {
                                            return (
                                                <TableRow key={el.id}>
                                                    <TableCell classes={tableStickyHeader} padding="none" align="center" style={{ width: 30 }}>
                                                        <EditButton callback={makeEdit} callbackParam={{ item: el }} />
                                                        <DeleteButton callback={makeDelete} callbackParam={{ id: el.id }} />
                                                    </TableCell>
                                                    <TableCell classes={tableStickyHeader} padding="none" align="center">
                                                        {el.mainname}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                </TableBody>
                                <TableFooter>
                                    <TableRow>
                                        <TableCell colSpan={10}>
                                            {templates && templates['hydra:view'] && <Pager callback={loadTemplates} views={templates} />}
                                        </TableCell>
                                    </TableRow>
                                </TableFooter>
                            </Table>
                        </CardBody>
                    </Card>
                </GridItem>
                <GridItem xs={6}>
                    <Card>
                        <CardHeader icon={<AccountTreeIcon />} trans="notifications:welcome_sms.zones" />
                        <CardBody>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell classes={tableStickyHeader} padding="none" align="center" style={{ width: 30 }}>
                                            <I18n trans="notifications:welcome_sms.actions" />
                                        </TableCell>
                                        <TableCell classes={tableStickyHeader} padding="none" align="center">
                                            <I18n trans="notifications:welcome_sms.schema_name" />
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {zones &&
                                        zones['hydra:member'].map((el: any) => {
                                            return (
                                                <TableRow key={el.id}>
                                                    <TableCell classes={tableStickyHeader} padding="none" align="center" style={{ width: 30 }}>
                                                        <EditButton callback={makeZoneEdit} callbackParam={{ item: el }} />
                                                        <DeleteButton callback={makeZoneDelete} callbackParam={{ id: el.id }} />
                                                    </TableCell>
                                                    <TableCell classes={tableStickyHeader} padding="none" align="center">
                                                        {el.mainname}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                </TableBody>
                                <TableFooter>
                                    <TableRow>
                                        <TableCell colSpan={10}>
                                            {zones && zones['hydra:view'] && <Pager callback={loadZones} views={zones} />}
                                        </TableCell>
                                    </TableRow>
                                </TableFooter>
                            </Table>
                        </CardBody>
                    </Card>
                </GridItem>
                <GridItem xs={12}>
                    {deleteMessage && (
                        <AlertDialog
                            open={!!deleteMessage}
                            titleDialog="notifications:h.delete_confirmation"
                            displayMessage={deleteMessage}
                            closeCallback={(): void => {
                                setDeleteMessage(null);
                                setSelectedIdToDelete(null);
                            }}
                            confirmCallback={confirmDelete}
                        />
                    )}
                </GridItem>
            </GridContainer>
        );
    }
};
export default withStyles(oplAppStyle)(WelcomeSmsConfiguration);
